import {format} from "date-fns"
import type {OtkDocumentDetail, OtkDocumentPhoto, OtkSubjectProfile, SignedOtkDocumentPayload} from "shared"

import type {DocumentPreviewSection} from "@/components/document-editor/DocumentCard"
import type {DocumentPreviewPrintData} from "@/components/document-editor/DocumentPreviewDialog"
import {displayBranchAddress, displaySubjectEntity, resolveInspectionBranch,} from "@/lib/api/subjects"

import {
  buildSignedOtkDocumentPayload,
  deriveOutcome,
  emissionTestModes,
  isNonConformitiesTabComplete,
  isOwnerTabComplete,
  isReequipmentTabComplete,
  isTestsTabComplete,
  isVehicleTabComplete,
  nonConformitiesStateFromDetail,
  type NonConformitiesTabState,
  ownerStateFromDetail,
  type OwnerTabState,
  reequipmentStateFromDetail,
  type ReequipmentTabState,
  severityLabels,
  testsStateFromDetail,
  type TestsTabState,
  vehicleStateFromDetail,
  type VehicleTabState,
  type YesNo,
} from "@/lib/documentEditor"

function yesNo(value: YesNo): string {
  return value === null ? "" : value ? "Так" : "Ні"
}

/**
 * The actual field-mapping logic behind `documentCardSections` below,
 * factored out so `DocumentEdit.tsx` can feed it the wizard's own *live*
 * tab state directly (for `DocumentPreviewDialog`'s "Перегляд Введених
 * Даних" tab) instead of going through a stored `OtkDocumentDetail`
 * round-trip it doesn't have yet for in-progress edits.
 */
export function documentCardSectionsFromStates({
  vehicle,
  owner,
  tests,
  nonConformities,
  reequipment,
}: {
  vehicle: VehicleTabState
  owner: OwnerTabState
  tests: TestsTabState
  nonConformities: NonConformitiesTabState
  reequipment: ReequipmentTabState
}): DocumentPreviewSection[] {
  const issueDescriptions = nonConformities.selectedCodes.map(
    (code) => `${code.code}: ${code.description}`
  )
  const unlistedDescriptions = nonConformities.unlistedIssues.map(
    (issue) => `${severityLabels[issue.type]}: ${issue.description}`
  )
  const conversionDescriptions = reequipment.conversions.map(
    (conversion) =>
      `${conversion.description} — ${
        conversion.approved
          ? `Погоджено: ${conversion.docName} ${conversion.docNumber} (${conversion.docDate})`
          : "Не погоджено"
      }`
  )

  return [
    {
      title: "1. Транспортний засіб",
      fields: [
        { label: "Номерний знак", value: vehicle.plate },
        { label: "VIN-код", value: vehicle.vin },
        { label: "Марка", value: vehicle.brand },
        { label: "Модель", value: vehicle.model },
        { label: "Рік випуску ТЗ", value: vehicle.manufactureYear },
        { label: "Категорія", value: vehicle.category },
        { label: "Показник одометра, км", value: vehicle.currentOdometer },
        {
          label: "Підлягає обов'язковому технічному контролю",
          value: yesNo(vehicle.subjectToInspection),
        },
        ...(vehicle.subjectToInspection !== false
          ? [
              {
                label: "Періодичність проходження ОТК",
                value: vehicle.periodicity,
              },
              {
                label: "Дата чергового проходження ОТК не пізніше",
                value: vehicle.nextInspectionDate,
              },
            ]
          : []),
        { label: "Призначення ТЗ", value: vehicle.purposes.join(", ") },
        {
          label: "Автомобіль експлуатується як таксі",
          value: yesNo(vehicle.isTaxi),
        },
        {
          label: "Міжнародний технічний огляд проведено",
          value: yesNo(vehicle.internationalInspection),
        },
        {
          label: "ТЗ обладнано тахографом",
          value: yesNo(vehicle.hasTachograph),
        },
        { label: "Примітки", value: vehicle.notes },
      ],
    },
    {
      title: "2. Власник і отримувач",
      fields: [
        {
          label: "Тип власника",
          value:
            owner.ownerType === "LEGAL_ENTITY"
              ? "Юридична особа"
              : "Фізична особа",
        },
        {
          label: "Власник",
          value:
            owner.ownerType === "LEGAL_ENTITY" ? owner.orgName : owner.fullName,
        },
        {
          label: "РНОКПП / ЄДРПОУ",
          value: owner.ownerType === "LEGAL_ENTITY" ? owner.edrpou : owner.taxId,
        },
        {
          label: "Отримувач",
          value:
            owner.recipientMode === "owner" ? "Власник" : owner.recFullName,
        },
      ],
    },
    {
      title: "3. Випробування",
      fields: [
        {
          label: "Питома сила гальмування робочої гальмівної системи",
          value: tests.totalBrakingForce,
        },
        {
          label: "Коефіцієнт нерівномірності гальмівних сил",
          value: tests.imbalanceRatio,
        },
        {
          label: "Час спрацьовування робочої гальмівної системи",
          value: tests.brakeResponseTime,
        },
        {
          label: "Питома сила стоянкової гальмівної системи",
          value: tests.parkingBrakeForce,
        },
        {
          label: "Режим випробування викидів",
          value:
            emissionTestModes.find((mode) => mode.value === tests.emissionMode)
              ?.label ?? "",
        },
        { label: "Димність дизеля, K", value: tests.smokeK },
      ],
    },
    {
      title: "4. Невідповідності",
      fields: [
        { label: "Коди невідповідностей", value: issueDescriptions.join("; ") },
        {
          label: "Додані невідповідності",
          value: unlistedDescriptions.join("; "),
        },
      ],
    },
    {
      title: "5. Переобладнання",
      fields: [
        { label: "Є переобладнання", value: yesNo(reequipment.hasReequipment) },
        { label: "Переобладнання", value: conversionDescriptions.join("; ") },
      ],
    },
  ]
}

/**
 * `DocumentModal`'s view-mode `DocumentCard` feed — mirrors
 * `otk/wireframes/src/pages/Details.tsx`'s `documentCardSections` (same five
 * sections, same field order/labels), but built from the real
 * `OtkDocumentDetail` shape via the same `*StateFromDetail` mappers
 * `DocumentEdit.tsx` uses to seed the wizard, so a value shown here always
 * agrees with what the wizard would show for the same document. Photos are
 * intentionally not a section here, same as the wireframes source: neither
 * this document's editor state nor `OtkDocumentDetail` carries a photo URL.
 */
export function documentCardSections(
  detail: OtkDocumentDetail
): DocumentPreviewSection[] {
  return documentCardSectionsFromStates({
    vehicle: vehicleStateFromDetail(detail),
    owner: ownerStateFromDetail(detail),
    tests: testsStateFromDetail(detail),
    nonConformities: nonConformitiesStateFromDetail(detail),
    reequipment: reequipmentStateFromDetail(detail),
  })
}

export interface DocumentSignReadiness {
  disabledReasons: string[]
  isNonconformityAct: boolean
}

// Mirrors `DocumentEdit.tsx`'s own `wizardSections`/`incompleteSections`
// completeness predicate, section for section, but evaluated against the
// stored `OtkDocumentDetail` snapshot instead of live wizard-form state,
// since view mode never mounts the wizard. Фото is intentionally omitted —
// same reason `vehicleStateFromDetail` & co. don't seed `PhotosTabState`:
// `OtkDocumentDetail` carries no uploaded photo file/URL to check against.
const SIGN_REQUIRED_SECTIONS: {
  label: string
  complete: (detail: OtkDocumentDetail) => boolean
}[] = [
  {
    label: "Транспортний Засіб",
    complete: (detail) => isVehicleTabComplete(vehicleStateFromDetail(detail)),
  },
  {
    label: "Власник і отримувач",
    complete: (detail) => isOwnerTabComplete(ownerStateFromDetail(detail)),
  },
  {
    label: "Випробування",
    complete: (detail) => isTestsTabComplete(testsStateFromDetail(detail)),
  },
  {
    label: "Невідповідності",
    complete: (detail) =>
      isNonConformitiesTabComplete(nonConformitiesStateFromDetail(detail)),
  },
  {
    label: "Переобладнання",
    complete: (detail) =>
      isReequipmentTabComplete(reequipmentStateFromDetail(detail)),
  },
]

export function documentSignReadiness(
  detail: OtkDocumentDetail
): DocumentSignReadiness {
  const disabledReasons = SIGN_REQUIRED_SECTIONS.filter(
    (section) => !section.complete(detail)
  ).map(
    (section) =>
      `Не заповнено обов'язкові поля в розділі «${section.label}».`
  )
  const outcome = deriveOutcome({
    selectedCodes: nonConformitiesStateFromDetail(detail).selectedCodes,
    unlistedIssues: nonConformitiesStateFromDetail(detail).unlistedIssues,
    conversions: reequipmentStateFromDetail(detail).conversions,
  })
  return {
    disabledReasons,
    isNonconformityAct: outcome.actReasons.length > 0,
  }
}

/**
 * View mode's `buildSignPayload` feed for `DocumentPreviewDialog`
 * (`DocumentModal.tsx`'s `ViewModeBody`) — built from the stored `detail`
 * snapshot via the same `*StateFromDetail` mappers `documentCardSections`/
 * `documentSignReadiness` already use, mirroring `DocumentEdit.tsx`'s own
 * live-state `buildSignPayload` (that one reads the wizard's in-progress tab
 * state instead, since view mode has no wizard). `files` is the caller's own
 * fresh `getOtkDocumentFilesWithUrl` result — this module has no store/API
 * access of its own.
 */
export function buildSignedOtkDocumentPayloadFromDetail(
  detail: OtkDocumentDetail,
  files: OtkDocumentPhoto[],
  selection: {
    electronicSeries: string
    electronicNumber: string
    blankSeries?: string
    blankNumber?: string
  }
): SignedOtkDocumentPayload {
  const isNonconformityAct = documentSignReadiness(detail).isNonconformityAct
  return buildSignedOtkDocumentPayload({
    type: isNonconformityAct ? "NONCONFORMITY_ACT" : "PROTOCOL",
    result: isNonconformityAct ? "NEGATIVE" : "POSITIVE",
    punktId: detail.punktId ?? "",
    vehicle: vehicleStateFromDetail(detail),
    owner: ownerStateFromDetail(detail),
    tests: testsStateFromDetail(detail),
    nonConformities: nonConformitiesStateFromDetail(detail),
    reequipment: reequipmentStateFromDetail(detail),
    files,
    electronicDocumentSeries: selection.electronicSeries,
    electronicDocumentNumber: selection.electronicNumber,
    blankSeries: selection.blankSeries,
    blankNumber: selection.blankNumber,
  })
}

/**
 * document-editor.md's printed-tab `fieldValues`: maps the live editor data
 * to every protocol v2 field (`documentPreviewFields.ts`'s
 * `protocolV2PrintedFields`) so `DocumentPreviewCanvas` can render the
 * actual document instead of its own mock examples. `inspectionPlace`/
 * `inspectionEntity`/`punktManager` deliberately do not come from
 * owner/document display strings — they're resolved from the subject
 * profile and the document's own пункт (falling back to the subject's
 * first пункт), per that spec's own note.
 */
function protocolFieldValues(
  documentNumber: string,
  protocolDate: Date,
  vehicle: VehicleTabState,
  subject: OtkSubjectProfile | null,
  punktId: string | undefined
): Record<string, string> {
  const branch = subject ? resolveInspectionBranch(subject, punktId) : undefined
  return {
    documentNumber,
    protocolDate: format(protocolDate, "dd.MM.yyyy"),
    nextInspectionDate: vehicle.nextInspectionDate
      ? format(new Date(vehicle.nextInspectionDate), "dd.MM.yyyy")
      : "",
    inspectionPlace: branch ? displayBranchAddress(branch) : "",
    inspectionEntity: subject ? displaySubjectEntity(subject) : "",
    vinOrChassisNumber: vehicle.vin || vehicle.chassisNumber,
    vehicleCategoryMakeModel: [vehicle.category, vehicle.brand, vehicle.model]
      .filter(Boolean)
      .join(", "),
    registrationPlate: vehicle.plate,
    odometer: vehicle.currentOdometer,
    stateRegistrationDate: vehicle.registrationDate
      ? format(new Date(vehicle.registrationDate), "dd.MM.yyyy")
      : "",
    ecologicalLevel: vehicle.ecologyLevel,
    cargoCapacity: vehicle.cargoCapacity,
    vehiclePurpose: vehicle.purposes.join(", "),
    punktManager: branch?.punktManager ?? "",
  }
}

/**
 * `DocumentModal` view mode's own `printData` feed for `DocumentPreviewDialog`
 * — same shape as `printDataFromVehicleState` below, built from a stored
 * `OtkDocumentDetail` snapshot instead of live wizard state. `subject` is the
 * caller's own `useSession` subject profile — this module has no store
 * access of its own.
 */
export function printDataFromDetail(
  detail: OtkDocumentDetail,
  subject: OtkSubjectProfile | null
): DocumentPreviewPrintData {
  return printDataFromVehicleState(
    detail.documentNumber,
    vehicleStateFromDetail(detail),
    documentSignReadiness(detail).isNonconformityAct,
    { protocolDate: new Date(detail.createdAt), punktId: detail.punktId, subject }
  )
}

/**
 * `DocumentEdit.tsx`'s own `printData` feed for `DocumentPreviewDialog` —
 * built from the wizard's *live* `VehicleTabState`, not a stored `detail`
 * snapshot (unlike `documentCardSections`/`documentSignReadiness` above),
 * since the dialog needs to reflect in-progress, not-yet-saved edits.
 */
export function printDataFromVehicleState(
  documentNumber: string,
  vehicle: VehicleTabState,
  isNonconformityAct: boolean,
  fieldContext: {
    protocolDate: Date
    subject: OtkSubjectProfile | null
    punktId: string | undefined
  }
): DocumentPreviewPrintData {
  return {
    documentNumber,
    vehicle: [vehicle.brand, vehicle.model, vehicle.plate]
      .filter(Boolean)
      .join(", "),
    result: isNonconformityAct ? "NEGATIVE" : "POSITIVE",
    fieldValues: protocolFieldValues(
      documentNumber,
      fieldContext.protocolDate,
      vehicle,
      fieldContext.subject,
      fieldContext.punktId
    ),
  }
}
